import { Button } from '@ksyos/design-system';
import { cloneDeep, isEqual } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import type {
    IAddCarePathSettings,
    IEpisodeCategoryBasic,
    IServiceGroupWithProfileSettings,
} from '@common/interfaces/';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IOrganizationProfile } from '../../../interfaces/organizationAddCarePathState';
import type { ISelectableOption } from '../../../interfaces/selectableOption';
import type { ISelectableCard } from '../../organizations/organizationAddCarePath/OrganizationAddCarePathContent';
import { addCarePathProfileSettingsSchema } from '../../../../../common/validation/addCarePath';
import { translateKey } from '../../../../translate';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { emptyValidationErrors, validate, validateOne } from '../../../../utils/validation';
import { PersonalSettingsAddCarePathContext } from '../../personalSettings/addCarePath/personalSettingsAddCarePathContext';
import { SelectableOptionsCard as RolesSelectableOptionsCard } from '../cards/SelectableOptionsCard';
import { CarePathSettingsModal } from '../modals/CarePathSettingsModal';

interface IServiceGroupWithAddCarePathSettings extends IServiceGroupWithProfileSettings {
    profileSettings?: IAddCarePathSettings;
}

interface IChooseRolesProps {
    employmentStartDate?: string;
    episodeCategory: IEpisodeCategoryBasic;
    sequence: number;
    serviceGroup: IServiceGroupWithAddCarePathSettings;
    updateState: (serviceGroup: IServiceGroupWithAddCarePathSettings) => void;
}

export function ChooseRoles(props: IChooseRolesProps) {
    const abortableFetch = useAbortableFetch();

    const [card, setCard] = useState<ISelectableCard>();
    const [roles, setRoles] = useState<IOrganizationProfile[]>([]);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [settings, setSettings] = useState<IAddCarePathSettings>();
    const [settingErrors, setSettingErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const { defaultProfileSettings, setDefaultProfileSettings } = useContext(PersonalSettingsAddCarePathContext);

    useEffect(() => {
        loadRoles();
    }, []);

    useEffect(() => {
        createCard();
    }, [props.serviceGroup, roles]);

    if (!card || !roles) {
        return null;
    }

    return (
        <>
            {showSettingsModal && settings && (
                <CarePathSettingsModal
                    employmentStartDate={props.employmentStartDate}
                    episodeCategory={props.episodeCategory}
                    errors={settingErrors}
                    onCancel={closeSettingsModal}
                    onChange={handleChangeSettings}
                    onSave={saveSettings}
                    settings={settings}
                />
            )}
            <RolesSelectableOptionsCard
                {...card}
                key={props.serviceGroup.id}
                onChange={handleChange}
            />
        </>
    );

    async function onClickSettings() {
        if (!props.serviceGroup.profileSettings) {
            return;
        }

        setSettings({ ...props.serviceGroup.profileSettings });
        setShowSettingsModal(!showSettingsModal);
    }

    function saveSettings() {
        const { errors } = validate(addCarePathProfileSettingsSchema, settings);

        if (errors.size > 0) {
            setSettingErrors(errors);
        } else {
            props.updateState({ ...props.serviceGroup, profileSettings: settings });
            closeSettingsModal();
        }
    }

    function handleChangeSettings(updatedSettings: IAddCarePathSettings, errorPath: string) {
        setSettings({ ...updatedSettings });
        validateOne(addCarePathProfileSettingsSchema, updatedSettings, errorPath, settingErrors, setSettingErrors);
    }

    function closeSettingsModal() {
        setSettingErrors(emptyValidationErrors);
        setSettings(undefined);
        setShowSettingsModal(!showSettingsModal);
    }

    async function handleChange(id: string) {
        const { profile, profileSettings, ...serviceGroup } = props.serviceGroup;

        if (profile?.id === id) {
            return props.updateState(serviceGroup);
        }

        const result = await abortableFetch.fetchJson(
            `/api/profiles/${id}/getDefaultSettings?includeSubsequentServices=true`,
            'GET'
        );
        const defaultSettings = result.json.items;

        const newProfile = roles.find((role) => role.id === id);

        setDefaultProfileSettings(cloneDeep(defaultSettings));
        props.updateState({ ...serviceGroup, profile: newProfile, profileSettings: cloneDeep(defaultSettings) });
    }

    function createCard() {
        if (!roles) {
            return;
        }

        const selectableOptions = roles.map((profile: IOrganizationProfile) => ({
            name: profile.name,
            id: profile.id,
            selected: props.serviceGroup.profile?.id === profile.id,
            displayName: profile.name,
        })) as ISelectableOption[];

        const serviceGroupCard: ISelectableCard = {
            title: translateKey('carePath.roleWithin', props.serviceGroup.name),
            sequence: props.sequence,
            selectableOptions,
            ...(props.serviceGroup.profile?.id ? { extraComponent: renderAdvancedButton() } : {}),
        };

        setCard(serviceGroupCard);
    }

    async function loadRoles() {
        const url = `/api/profiles?serviceGroupIds[]=${props.serviceGroup.id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        setRoles(result.json.items);
    }

    function renderAdvancedButton() {
        return (
            <Button
                iconLeft="settings"
                onClick={onClickSettings}
                size="medium"
                sx={{ mt: 2 }}
                variant="link"
            >
                {isEqual(defaultProfileSettings, props.serviceGroup.profileSettings)
                    ? translateKey('common.advanced')
                    : translateKey('common.advancedUpdated')}
            </Button>
        );
    }
}
